/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import Drawer from '@atlaskit/drawer';
import { Editor, EditorContext, WithEditorActions } from '@atlaskit/editor-core';
import { css } from '@emotion/react';
import PageHeader from '@atlaskit/page-header';
import Button, { LoadingButton, ButtonGroup } from '@atlaskit/button';
import { queryCache, useQuery } from 'react-query';
import { DrawerBody, DrawerContainer, DrawerFooter, DrawerHeader } from '../../components/drawer';
import { FormattedMessage } from '../../components/i18n';
import api from '../../libs/api';
import { useCurrentCompany } from '../auth/CurrentCompany';

type Context = {
  isNotesOpen: boolean;
  openNotes: ()=> void;
  hideNotes: ()=> void;
  toggleNotes: () => void;
};

export const NotesContext = React.createContext<Context>({
  isNotesOpen: false,
  openNotes: () => {},
  hideNotes: () => {},
  toggleNotes: () => {}
});

type Props = {
  isOpen: boolean;
  onClose: ()=> void;
};

const fetchNotes = async (key:string, companyId: string) =>
  api.get(`/Companies/${companyId}/notes`);

const saveNotes = async (companyId: string, data: any) =>
  api.put(`/Companies/${companyId}/notes`, data);

export const Notes: React.FC<Props> = ({ isOpen, onClose }) => {
  const currentCompany = useCurrentCompany();
  const { data, isLoading, refetch } = useQuery(['notes', currentCompany!.id], fetchNotes, {});
  const notes = data?.data;

  const editor = useMemo(() => <Editor appearance="full-page" defaultValue={notes} />, [notes]);
  const onSave = useCallback((actions) => {

    actions.getValue().then(async (value: any) => {
      if (value != null) {
        console.log('value is', value);
      }
      await saveNotes(currentCompany!.id, value);
      queryCache.invalidateQueries(['notes', currentCompany!.id]);
      onClose();
    });
  }, []);
  return (
    <EditorContext>
      <WithEditorActions render={actions => (
        <Drawer isOpen={isOpen} onClose={() => onSave(actions)} width="wide">
          <DrawerContainer>
            <DrawerHeader>
              <PageHeader>Notities</PageHeader>
            </DrawerHeader>
            <DrawerBody>
              <div css={css`padding-right: 40px; 
          .ak-editor-content-area{
          padding: 0px;
          width: 100%;
          }
          .akEditor > div:nth-child(2) {
            width: 100%;
          }
          .akEditor > div:nth-child(2) > div:first-child {
            width: 100%;
          }
          //.akEditor div:nth-child(2) > div {
          //  background: green;
          //  width: 100%;
          //}
          
          //.fabric-editor-popup-scroll-parent {
          // > div > div {
          //    padding-top: 25px;
          //}
          //}
      `}
              >
                {editor}
              </div>
            </DrawerBody>
            <DrawerFooter>
              <div
                css={css`
              display: flex;
              justify-content: flex-end;
            `}
              >
                <ButtonGroup>
                  <LoadingButton
                    appearance="primary"
                    isLoading={false}
                    onClick={()=>onSave(actions)}
                  >
                    <FormattedMessage id="common.save" defaultMessage="Save" />
                  </LoadingButton>
                  <Button appearance="subtle" onClick={()=>onClose()}><FormattedMessage id="common.cancel" defaultMessage="Cancel" /></Button>
                </ButtonGroup>
              </div>
            </DrawerFooter>
          </DrawerContainer>
        </Drawer>
      )}
      />
    </EditorContext>
  );
};

export const NotesProvider: React.FC = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hideNotes = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openNotes = useCallback(() => {
    setIsOpen(true);
  }, []);

  const toggleNotes = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const value = useMemo(() => ({
    isNotesOpen: isOpen,
    hideNotes,
    openNotes,
    toggleNotes,
  }), [isOpen]);

  // @ts-ignore
  window.toggleNotes = toggleNotes;

  return (
    <NotesContext.Provider
      value={value}
    >
      <>
        <Notes isOpen={isOpen} onClose={() => hideNotes()} />
        {children}
      </>
    </NotesContext.Provider>
  );
};


